import React, { useMemo, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { X, Clock, Trash2, Tag, Gift, Store, Image as ImageIcon, ThumbsUp, ThumbsDown, MapPin, CheckCircle2, AlertTriangle, Share2, Check, Flag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { fileUrl, BACKEND_URL } from '@/lib/api';
import api from '@/lib/api';
import { toast } from 'sonner';
import ReportDialog from '@/components/ReportDialog';

const META = {
  free: { labelKey: 'detail.free', color: '#7A9A78', Icon: Gift },
  yard_sale: { labelKey: 'detail.yardSale', color: '#D99E4A', Icon: Store },
};

function fmtDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
function fmtTime(iso) {
  const d = new Date(iso);
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

export default function PinDetailPanel({ pin: initialPin, onClose, onDeleted, canEdit, canReport, onEdit }) {
  const { t } = useTranslation();
  const [pin, setPin] = useState(initialPin);
  useEffect(() => { setPin(initialPin); }, [initialPin]);
  const meta = META[pin.category];
  const Icon = meta.Icon;
  const isNew = (Date.now() - new Date(pin.created_at).getTime()) < 24 * 3600 * 1000;
  const endsInMs = new Date(pin.ends_at).getTime() - Date.now();
  const endingSoon = pin.status === 'active' && endsInMs > 0 && endsInMs < 24 * 3600 * 1000;
  const [activePhoto, setActivePhoto] = useState(0);
  const [voting, setVoting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.matchMedia('(max-width: 640px)').matches
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 640px)');
    const h = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', h);
    return () => mq.removeEventListener('change', h);
  }, []);

  const handleShare = async () => {
    const url = `${BACKEND_URL}/api/share/${pin.id}`;
    const shareData = { title: pin.title, text: `Check out "${pin.title}" on CurbAlert`, url };
    try {
      if (navigator.share && /Mobi|Android/i.test(navigator.userAgent)) {
        await navigator.share(shareData);
        return;
      }
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success(t('detail.linkCopied'));
      setTimeout(() => setCopied(false), 2000);
    } catch (e) {
      if (e?.name === 'AbortError') return;
      toast.error(t('detail.couldNotShare'));
    }
  };

  const statusLabel = useMemo(() => {
    if (pin.status === 'upcoming') return { text: `${t('detail.upcoming')} · ${fmtDate(pin.starts_at)}${pin.category === 'yard_sale' ? ' · ' + fmtTime(pin.starts_at) : ''}`, color: '#D99E4A' };
    if (pin.status === 'active') {
      const end = `${t('detail.activeUntil')} ${fmtDate(pin.ends_at)}${pin.category === 'yard_sale' ? ' ' + fmtTime(pin.ends_at) : ''}`;
      return { text: end, color: '#7A9A78' };
    }
    if (pin.status === 'hidden') return { text: t('detail.hidden'), color: '#C86F53' };
    return { text: t('detail.ended'), color: '#8A8F87' };
  }, [pin.status, pin.starts_at, pin.ends_at, pin.category, t]);

  const handleDelete = async () => {
    if (!window.confirm(t('detail.confirmDelete'))) return;
    try { await api.delete(`/pins/${pin.id}`); toast.success(t('detail.deleted')); onDeleted && onDeleted(); }
    catch { toast.error(t('detail.couldNotDelete')); }
  };

  const vote = async (value) => {
    setVoting(true);
    try {
      const r = await api.post(`/pins/${pin.id}/vote`, { value });
      setPin(r.data);
      toast.success(value === 'not_here' ? t('detail.voteGone') : t('detail.voteHere'));
      if (r.data.status === 'hidden') toast.message(t('detail.voteHidden'));
    } catch (e) { toast.error(e.response?.data?.detail || t('detail.couldNotVote')); }
    finally { setVoting(false); }
  };

  return (
    <motion.div data-testid="pin-detail-panel"
      initial={isMobile ? { y: '100%' } : { x: 460, opacity: 0 }}
      animate={isMobile ? { y: 0 } : { x: 0, opacity: 1 }}
      exit={isMobile ? { y: '100%' } : { x: 460, opacity: 0 }}
      transition={{ type: 'spring', damping: 30, stiffness: 280 }}
      drag={isMobile ? 'y' : false}
      dragConstraints={{ top: 0, bottom: 0 }}
      dragElastic={{ top: 0, bottom: 0.4 }}
      onDragEnd={(e, info) => { if (isMobile && info.offset.y > 120) onClose(); }}
      className={isMobile
        ? 'fixed inset-x-0 bottom-0 z-[1100] glass-panel rounded-t-3xl overflow-hidden max-h-[88vh] flex flex-col'
        : 'absolute top-32 right-4 sm:right-6 z-[700] w-[calc(100%-2rem)] sm:w-96 glass-panel rounded-3xl overflow-hidden max-h-[80vh] flex flex-col'}>
      {isMobile && <div className="mx-auto mt-2.5 mb-1 h-1.5 w-12 rounded-full bg-black/15 shrink-0" data-testid="sheet-drag-handle" />}
      <div className="relative shrink-0">
        {pin.photos?.length > 0 ? (
          <div className="bg-black/5 aspect-[16/10] overflow-hidden">
            <img src={fileUrl(pin.photos[activePhoto])} alt={pin.title} className="w-full h-full object-cover" />
          </div>
        ) : (
          <div className="aspect-[16/10] bg-gradient-to-br from-[#eaeee5] to-[#dfe6d8] flex items-center justify-center"><ImageIcon className="w-10 h-10 text-brand-dim" /></div>
        )}
        <button data-testid="close-detail-btn" onClick={onClose} className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow"><X className="w-4 h-4 text-brand-ink" /></button>
        <button data-testid="share-pin-btn" onClick={handleShare} title={t('detail.shareTitle')} className="absolute top-3 right-12 w-8 h-8 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow">
          {copied ? <Check className="w-4 h-4 text-[#7A9A78]" /> : <Share2 className="w-4 h-4 text-brand-ink" />}
        </button>
      </div>

      <div className="p-5 sm:p-6 overflow-y-auto scroll-soft">
        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-widest"
            style={{ background: meta.color + '22', color: meta.color }} data-testid="pin-category-badge">
            <Icon className="w-3.5 h-3.5" /> {t(meta.labelKey)}
          </span>
          {pin.price != null && <span className="text-xs font-semibold px-2 py-1 rounded-full bg-black/5">${pin.price.toFixed(2)}</span>}
          {isNew && <span data-testid="pin-tag-new" className="text-xs font-semibold px-2.5 py-1 rounded-full" style={{ background: '#7A9A7822', color: '#5C7A5A' }}>{t('detail.new')}</span>}
          {endingSoon && <span data-testid="pin-tag-ending" className="text-xs font-semibold px-2.5 py-1 rounded-full" style={{ background: '#C86F5322', color: '#C86F53' }}>{t('detail.endingSoon')}</span>}
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium" data-testid="pin-status"
            style={{ background: statusLabel.color + '18', color: statusLabel.color }}>
            <Clock className="w-3 h-3" /> {statusLabel.text}
          </span>
        </div>

        <h3 className="font-display font-bold text-2xl tracking-tight text-brand-ink leading-tight" data-testid="pin-title">{pin.title}</h3>
        {pin.description && <p className="text-brand-slate text-sm mt-2 whitespace-pre-wrap" data-testid="pin-description">{pin.description}</p>}

        {pin.address && (
          <div className="mt-3 flex items-start gap-1.5 text-xs text-brand-slate"><MapPin className="w-3.5 h-3.5 mt-0.5 text-brand" /> <span>{pin.address}</span></div>
        )}

        {canEdit && pin.status === 'hidden' && (
          <div className="mt-4 rounded-2xl p-3 bg-[#C86F53]/10 border border-[#C86F53]/20">
            <div className="flex items-center gap-2 text-[#C86F53] text-sm font-semibold"><AlertTriangle className="w-4 h-4" /> {t('detail.reportedGone')}</div>
            <p className="text-xs text-brand-slate mt-1">{t('detail.reportedGoneHint')}</p>
            <Button data-testid="confirm-still-here-btn" onClick={async () => {
              const r = await api.post(`/pins/${pin.id}/confirm`); setPin(r.data); toast.success(t('detail.stillHereVisible'));
            }} className="mt-2 rounded-full bg-brand hover:bg-brand-hover text-white" size="sm">
              <CheckCircle2 className="w-4 h-4 mr-1" /> {t('detail.stillHere')}
            </Button>
          </div>
        )}

        {!canEdit && (pin.status === 'active' || pin.status === 'upcoming') && (
          <div className="mt-5 rounded-2xl p-3 bg-black/5">
            <div className="text-xs font-semibold text-brand-ink mb-2">
              {pin.category === 'yard_sale' ? t('detail.stillQuestionYard') : t('detail.stillQuestionItem')}
            </div>
            <div className="flex gap-2">
              <Button data-testid="vote-still-here-btn" disabled={voting} onClick={() => vote('still_here')} variant="outline"
                className={`rounded-full text-sm flex-1 ${pin.my_vote === 'still_here' ? 'bg-[#7A9A78]/15 border-[#7A9A78] text-[#7A9A78]' : ''}`}>
                <ThumbsUp className="w-4 h-4 mr-1" /> {t('detail.yes')} ({pin.still_here_count || 0})
              </Button>
              <Button data-testid="vote-not-here-btn" disabled={voting} onClick={() => vote('not_here')} variant="outline"
                className={`rounded-full text-sm flex-1 ${pin.my_vote === 'not_here' ? 'bg-[#C86F53]/15 border-[#C86F53] text-[#C86F53]' : ''}`}>
                <ThumbsDown className="w-4 h-4 mr-1" /> {t('detail.no')} ({pin.not_here_count || 0})
              </Button>
            </div>
            <div className="text-[11px] text-brand-dim mt-1.5">{t('detail.voteHint')}</div>
          </div>
        )}

        <div className="mt-5 pt-4 border-t border-black/5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-brand text-white text-xs flex items-center justify-center">{pin.user_name?.[0]?.toUpperCase() || 'N'}</div>
            <div className="text-xs text-brand-slate">@{pin.user_name}</div>
          </div>
          {canEdit && (
            <div className="flex items-center gap-1">
              <Button data-testid="edit-pin-btn" variant="ghost" onClick={() => onEdit && onEdit(pin)} className="rounded-full text-brand hover:bg-brand/10">
                {t('detail.edit')}
              </Button>
              <Button data-testid="delete-pin-btn" variant="ghost" onClick={handleDelete} className="rounded-full text-[#C86F53] hover:bg-[#C86F53]/10">
                <Trash2 className="w-4 h-4 mr-1" /> {t('detail.delete')}
              </Button>
            </div>
          )}
        </div>

        <a
          data-testid="directions-link"
          href={`https://www.google.com/maps/dir/?api=1&destination=${pin.lat},${pin.lng}`}
          target="_blank" rel="noopener noreferrer"
          className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-ink text-white px-4 py-2.5 text-sm font-medium hover:bg-black"
        >
          <MapPin className="w-4 h-4" /> {t('detail.getDirections')}
        </a>

        {canReport && (
          <button
            data-testid="report-pin-btn"
            onClick={() => setReportOpen(true)}
            className="mt-2 w-full inline-flex items-center justify-center gap-1.5 text-xs text-brand-dim hover:text-[#C86F53] transition py-1.5"
          >
            <Flag className="w-3.5 h-3.5" /> {t('detail.reportAd')}
          </button>
        )}
      </div>

      {canReport && <ReportDialog open={reportOpen} onOpenChange={setReportOpen} pin={pin} />}
    </motion.div>
  );
}
